import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Alert
} from '@mui/material';
import SEO from '../components/common/SEO';

interface FormData {
  name: string;
  email: string;
  phone: string;
  message: string;
}

const Contact: React.FC = () => {
  const location = useLocation();
  const formRef = useRef<HTMLDivElement>(null);
  
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [submitError, setSubmitError] = useState<string>('');

  // Handle URL parameters for prefilling form
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const speakerName = urlParams.get('speaker');
    
    if (speakerName) {
      setFormData(prev => ({
        ...prev,
        message: `Interested in learning more about ${speakerName}`
      }));
      
      // Auto-scroll to form after a brief delay
      setTimeout(() => {
        formRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 500);
    }
  }, [location.search]);

  // Dark mode styles for form fields
  const darkFieldStyles = {
    '& .MuiOutlinedInput-root': {
      color: 'white',
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.3)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.5)',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'white',
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
    '& .MuiInputBase-input::placeholder': {
      color: 'rgba(255, 255, 255, 0.5)',
      opacity: 1,
    },
    '& .MuiSelect-icon': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name as keyof FormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.message.trim()) newErrors.message = 'Message is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError('');
    setShowSuccess(false);

    try {
      // Use production API endpoint for development since there's no local Cloudflare Functions server
      const apiUrl = process.env.NODE_ENV === 'development' 
        ? 'https://ceo-keynotes.pages.dev/api/contact'
        : '/api/contact';
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }

      if (result.success) {
        setShowSuccess(true);
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: ''
        });

        // Hide success message after 10 seconds
        setTimeout(() => {
          setShowSuccess(false);
        }, 10000);
      } else {
        throw new Error(result.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Failed to send message. Please try again.';
      
      // Handle rate limiting specifically
      if (errorMessage.includes('Too many requests')) {
        setSubmitError('Too many requests. Please wait 15 minutes before trying again.');
      } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
        setSubmitError('Network error. Please check your connection and try again.');
      } else {
        setSubmitError(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO 
        title="Contact CEO Keynotes - Book Your Speaker Today"
        description="Ready to book a keynote speaker? Contact CEO Keynotes for speaker availability, pricing, and booking assistance. Get your personalized quote today."
        keywords="contact, book speaker, keynote booking, speaker inquiry, CEO Keynotes contact, speaker availability, pricing, booking assistance"
        url="/contact"
      />
      
      <Box sx={{ 
        bgcolor: 'black', 
        minHeight: '100vh',
        color: 'white'
      }}>
        <Container maxWidth="lg" sx={{ py: 4, pt: 16 }}>
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <Typography 
              variant="h2" 
              component="h1" 
              gutterBottom
              sx={{
                fontFamily: '"Manrope", sans-serif',
                fontWeight: 700,
                mb: 3,
                color: 'white',
                fontSize: {
                  xs: '1.8rem',  
                  sm: '2.0rem',    
                  md: '2.4rem'   
                }
              }}
            >
              Contact Us
            </Typography>
            <Typography 
              variant="h5" 
              sx={{
                fontFamily: '"Newsreader", serif',
                maxWidth: '700px',
                mx: 'auto',
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: {
                  xs: '1.0rem',  
                  sm: '1.1rem',    
                  md: '1.25rem'  
                }
              }}
            >
              We're here to help find an unforgettable speaker for your event.
            </Typography>
          </Box>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 6 }}>
          {/* Contact Form */}
          <Box sx={{ flex: { md: 2 } }} ref={formRef}>
            <Paper elevation={2} sx={{ 
              p: 4,
              bgcolor: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px'
            }}>
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom
                sx={{
                  fontFamily: '"Manrope", sans-serif',
                  fontWeight: 600,
                  mb: 3,
                  color: 'white'
                }}
              >
                Send us a message
              </Typography>

              <Box component="form" onSubmit={handleSubmit}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }}>
                    <TextField
                      fullWidth
                      label="Name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      error={!!errors.name}
                      helperText={errors.name}
                      required
                      sx={darkFieldStyles}
                    />
               
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      error={!!errors.email}
                      helperText={errors.email}
                      required
                      sx={darkFieldStyles}
                    />
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      sx={darkFieldStyles}
                    />
                  </Box>
                  
                  <TextField
                    fullWidth
                    label="Message"
                    name="message"
                    multiline
                    rows={6}
                    value={formData.message}
                    onChange={handleInputChange}
                    error={!!errors.message}
                    helperText={errors.message}
                    placeholder="Tell us about your event, audience, and any specific requirements..."
                    required
                    sx={darkFieldStyles}
                  />
                  
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={isSubmitting}
                    sx={{
                      px: 6,
                      py: 1.5,
                      fontFamily: '"Manrope", sans-serif',
                      fontWeight: 600,
                      fontSize: '1.1rem',
                      textTransform: 'none',
                      borderRadius: 2,
                      alignSelf: 'flex-start',
                      bgcolor: 'white',
                      color: 'black',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                      },
                      '&:disabled': {
                        bgcolor: 'rgba(255, 255, 255, 0.5)',
                        color: 'rgba(0, 0, 0, 0.5)',
                      },
                    }}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>

                  {showSuccess && (
                    <Alert 
                      severity="success" 
                      sx={{ mt: 3 }}
                      onClose={() => setShowSuccess(false)}
                    >
                      Thank you for your message! We'll get back to you soon.
                    </Alert>
                  )}

                  {submitError && (
                    <Alert 
                      severity="error" 
                      sx={{ mt: 3 }}
                      onClose={() => setSubmitError('')}
                    >
                      {submitError}
                    </Alert>
                  )}
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Contact Information */}
          <Box sx={{ flex: 1, pl: { md: 2 } }}>
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom
              sx={{
                fontFamily: '"Manrope", sans-serif',
                fontWeight: 600,
                mb: 3,
                color: 'white'
              }}
            >
              Get in touch
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ fontFamily: '"Manrope", sans-serif', fontWeight: 600, color: 'white' }}>
                Response Time
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: '"Newsreader", serif', color: 'rgba(255, 255, 255, 0.7)' }}>
                We value timely communication and respond to inquiries as quickly as possible.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ fontFamily: '"Manrope", sans-serif', fontWeight: 600, color: 'white' }}>
                Speaker Availability
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: '"Newsreader", serif', color: 'rgba(255, 255, 255, 0.7)' }}>
                Our speakers are available for corporate events, conferences, and special occasions worldwide.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ fontFamily: '"Manrope", sans-serif', fontWeight: 600, color: 'white' }}>
                Custom Solutions
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: '"Newsreader", serif', color: 'rgba(255, 255, 255, 0.7)' }}>
                Need something specific? We work with you to create tailored speaking experiences that meet your unique needs.
              </Typography>
            </Box>
          </Box>
        </Box>
        </Container>
      </Box>
    </>
  );
};

export default Contact; 